import { CornerDownRight, Plus, SlidersHorizontal, Trash2 } from "lucide-react";
import { useState } from "react";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { useConfirm } from "../../components/ui/ConfirmDialog";
import { EmptyState, Skeleton } from "../../components/ui/Feedback";
import { Input, Select } from "../../components/ui/Input";
import { Switch } from "../../components/ui/Switch";
import { useToast } from "../../components/ui/Toast";
import { useUpdateGift } from "./useTiers";
import { ItemSettingsDialog } from "./ItemSettingsDialog";
import { Hint } from "../../components/ui/Tooltip";
import type { GiftProductNode } from "../../lib/types";
import { categoryStep, distinctCategories } from "./giftCategories";
import { OptionCategoryField } from "./OptionCategoryField";
import { OptionGuideField } from "./OptionGuideField";
import { ProductPicker, type PickedProduct } from "./ProductPicker";
import {
	useAddGiftProduct,
	useCreateOption,
	useDeleteGiftProduct,
	useDeleteOption,
	useGiftOptions,
	useUpdateGiftProduct,
} from "./useGiftOptions";

interface PickerTarget {
	/** Null when the product needs a new slot creating for it first. */
	optionId: string | null;
	role: "primary" | "backup";
	parentId: string | null;
	title: string;
	entryId?: string;
}

const BACKUP_HELP =
	"Tried in order, top first. The first one with stock is given instead, including when giving the product away would break into reserved stock.";

const titleOf = (node: GiftProductNode): string => node.product.title ?? "Untitled";

/**
 * What the shopper ends up with, in a sentence. Reading the configuration back
 * is the only way a merchant can check it without going to the storefront.
 */
const describeGift = (
	options: readonly { products: GiftProductNode[]; category: string | null }[],
	single: boolean,
	autoAdd: boolean,
): string | null => {
	const primaries = options
		.map((option) => option.products.find((node) => node.role === "primary"))
		.filter((node): node is GiftProductNode => node !== undefined);

	if (primaries.length === 0) return null;

	const names = primaries.map(titleOf);
	const lead =
		primaries.length === 1
			? `Shoppers get ${names[0]}`
			: single
				? `Shoppers pick one of ${names.length}: ${names.join(", ")}`
				: `Shoppers get all ${names.length}: ${names.join(", ")}`;

	const clauses: string[] = [];
	const tiles = single ? categoryStep(options.map((option) => option.category)) : null;
	if (tiles) clauses.push(`choosing a category first (${tiles.join(", ")})`);
	if (primaries.some((node) => node.selectVariant)) clauses.push("choosing the variant");
	if (primaries.some((node) => node.children.length > 0)) clauses.push("bundled items included");
	if (primaries.some((node) => node.subscriptionMode === "required")) {
		clauses.push("on a subscription only");
	}
	if (autoAdd) clauses.push("added to the cart for them");

	return clauses.length > 0 ? `${lead} — ${clauses.join(", ")}.` : `${lead}.`;
};

const VariantSummary = ({ node }: { node: GiftProductNode }) =>
	node.selectVariant ? (
		<Badge variant="outline">Shopper picks</Badge>
	) : (
		<Badge variant="quiet">{node.variant?.title ?? "Variant removed"}</Badge>
	);

const QuantityField = ({
	node,
	onChange,
}: {
	node: GiftProductNode;
	onChange: (quantity: number) => void;
}) => {
	const [value, setValue] = useState(String(node.quantity));

	const commit = () => {
		const quantity = Math.max(1, Math.trunc(Number(value) || 1));
		setValue(String(quantity));
		if (quantity !== node.quantity) onChange(quantity);
	};

	return (
		<label className="flex items-center gap-1.5">
			<span className="text-xs text-ink-faint">Qty</span>
			<Input
				type="number"
				min="1"
				step="1"
				value={value}
				aria-label="Quantity given"
				onChange={(event) => setValue(event.target.value)}
				onBlur={commit}
				className="h-8 w-16"
			/>
		</label>
	);
};

const ProductRow = ({
	node,
	depth,
	onAddBundle,
	onChange,
	onQuantity,
	onSettings,
	onRemove,
}: {
	node: GiftProductNode;
	depth: number;
	onAddBundle: (parentId: string) => void;
	onChange: (node: GiftProductNode) => void;
	onQuantity: (node: GiftProductNode, quantity: number) => void;
	onSettings: (node: GiftProductNode) => void;
	onRemove: (node: GiftProductNode) => void;
}) => (
	<>
		<div className="flex items-center gap-3 py-2" style={{ paddingLeft: `${depth * 1.5}rem` }}>
			{depth > 0 ? (
				<CornerDownRight className="h-3.5 w-3.5 shrink-0 text-ink-faint" strokeWidth={1.75} />
			) : null}
			{node.product.thumbnailUrl ? (
				<img
					src={node.product.thumbnailUrl}
					alt=""
					loading="lazy"
					className="h-8 w-8 shrink-0 rounded-md border border-line object-cover"
				/>
			) : (
				<div className="h-8 w-8 shrink-0 rounded-md border border-dashed border-line" />
			)}
			<div className="min-w-0 flex-1">
				<p className="truncate text-sm text-ink">{node.product.title ?? "Untitled"}</p>
				<p className="truncate text-xs text-ink-faint">{node.product.handle ?? "—"}</p>
			</div>
			<QuantityField node={node} onChange={(quantity) => onQuantity(node, quantity)} />
			<VariantSummary node={node} />
			<Button variant="ghost" size="sm" onClick={() => onSettings(node)}>
				<SlidersHorizontal className="h-3.5 w-3.5" strokeWidth={1.75} />
				Settings
			</Button>
			<Button variant="ghost" size="sm" onClick={() => onChange(node)}>
				Change
			</Button>
			{depth === 0 ? (
				<Button variant="ghost" size="sm" onClick={() => onAddBundle(node.id)}>
					<Plus className="h-3.5 w-3.5" strokeWidth={2} />
					Also add
				</Button>
			) : null}
			<Button variant="ghost" size="sm" aria-label="Remove product" onClick={() => onRemove(node)}>
				<Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
			</Button>
		</div>
		{node.children.map((child) => (
			<ProductRow
				key={child.id}
				node={child}
				depth={depth + 1}
				onAddBundle={onAddBundle}
				onChange={onChange}
				onQuantity={onQuantity}
				onSettings={onSettings}
				onRemove={onRemove}
			/>
		))}
	</>
);

export const GiftEditor = ({ campaignId, giftId }: { campaignId: string; giftId: string }) => {
	const options = useGiftOptions(campaignId, giftId);
	const createOption = useCreateOption(campaignId, giftId);
	const deleteOption = useDeleteOption(campaignId, giftId);
	const addProduct = useAddGiftProduct(campaignId, giftId);
	const updateProduct = useUpdateGiftProduct(campaignId, giftId);
	const deleteProduct = useDeleteGiftProduct(campaignId, giftId);
	const updateGift = useUpdateGift(campaignId);
	const toast = useToast();
	const confirm = useConfirm();
	const [target, setTarget] = useState<PickerTarget | null>(null);

	const notify = { onError: (error: unknown) => toast.push((error as Error).message, "error") };

	const pick = (picked: PickedProduct) => {
		if (!target) return;

		if (target.entryId) {
			updateProduct.mutate(
				{ entryId: target.entryId, ...picked },
				{
					onSuccess: () => {
						toast.push("Selection updated");
						setTarget(null);
					},
					...notify,
				},
			);
			return;
		}

		const add = (optionId: string) =>
			addProduct.mutate(
				{ optionId, role: target.role, parentId: target.parentId, ...picked },
				{
					onSuccess: () => {
						toast.push("Product added");
						setTarget(null);
					},
					...notify,
				},
			);

		if (target.optionId) return add(target.optionId);

		createOption.mutate(null, {
			onSuccess: (created) => add((created as { option: { id: string } }).option.id),
			...notify,
		});
	};

	const [settingsFor, setSettingsFor] = useState<GiftProductNode | null>(null);

	const saveSettings = (entryId: string, settings: Record<string, unknown>) =>
		updateProduct.mutate(
			{ entryId, ...settings },
			{
				onSuccess: () => {
					toast.push("Item saved");
					setSettingsFor(null);
				},
				...notify,
			},
		);

	const setQuantity = (node: GiftProductNode, quantity: number) =>
		updateProduct.mutate(
			{ entryId: node.id, quantity },
			{ onSuccess: () => toast.push("Quantity updated"), ...notify },
		);

	const removeProduct = (node: GiftProductNode) =>
		confirm({
			title: `Remove “${node.product.title ?? "this product"}”?`,
			description:
				node.children.length > 0
					? "Its bundled items will be removed too."
					: "You can add it back at any time.",
			confirmLabel: "Remove",
			onConfirm: () =>
				deleteProduct.mutate(node.id, {
					onSuccess: () => toast.push("Product removed"),
					...notify,
				}),
		});

	if (options.isPending) return <Skeleton className="h-40 w-full" />;

	const gift = options.data?.gift;
	const items = options.data?.items ?? [];
	const single = gift?.selection === "single";
	// Mirrors the storefront rule: a gift can be handed over without asking when
	// nothing in it needs a decision. Several pinned products are fine, so long
	// as the shopper is not being asked to pick between them.
	const primaries = items.map((option) => option.products.find((node) => node.role === "primary"));
	const canAutoAdd =
		primaries.length > 0 &&
		!(single && primaries.length > 1) &&
		primaries.every((node) => node && !node.selectVariant && node.subscriptionMode !== "optional");
	const summary = describeGift(items, single, gift?.autoAdd ?? false);
	const categoryListId = `categories-${giftId}`;

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-center justify-between gap-3">
				{items.length > 1 ? (
					<label className="flex items-center gap-2 text-sm text-ink">
						Shoppers get
						<Select
							className="h-8 w-auto"
							value={gift?.selection ?? "all"}
							onChange={(event) =>
								updateGift.mutate(
									{ giftId, selection: event.target.value as "single" | "all" },
									{ onSuccess: () => toast.push("Saved"), ...notify },
								)
							}
						>
							<option value="all">all of these</option>
							<option value="single">one of these, their pick</option>
						</Select>
					</label>
				) : (
					<span />
				)}
				<Button
					variant="outline"
					size="sm"
					disabled={createOption.isPending || addProduct.isPending}
					onClick={() =>
						setTarget({
							optionId: null,
							role: "primary",
							parentId: null,
							title: items.length === 0 ? "Choose the gift" : "Add another product",
						})
					}
				>
					<Plus className="h-3.5 w-3.5" strokeWidth={2} />
					{items.length === 0 ? "Choose product" : "Add another"}
				</Button>
			</div>

			{single ? (
				<datalist id={categoryListId}>
					{distinctCategories(items.map((option) => option.category)).map((entry) => (
						<option key={entry} value={entry} />
					))}
				</datalist>
			) : null}

			{summary ? (
				<p className="rounded-md border border-line bg-raised px-3 py-2 text-xs text-ink">
					{summary}
				</p>
			) : null}

			{items.length === 0 ? (
				<EmptyState
					title="Nothing in this gift yet"
					description="Choose what a shopper receives."
				/>
			) : null}

			{items.map((option, index) => {
				const primary = option.products.filter((node) => node.role === "primary");
				const backups = option.products.filter((node) => node.role === "backup");

				return (
					<div key={option.id} className="rounded-md border border-line bg-surface">
						{items.length > 1 ? (
							<div className="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
								<span className="flex min-w-0 items-center gap-3">
									<span className="text-xs font-medium text-ink">
										{option.label ?? (single ? `Choice ${index + 1}` : `Item ${index + 1}`)}
									</span>
									{single ? (
										<OptionCategoryField
											campaignId={campaignId}
											giftId={giftId}
											optionId={option.id}
											key={option.category ?? ""}
											category={option.category}
											listId={categoryListId}
										/>
									) : null}
								</span>
								<Button
									variant="ghost"
									size="sm"
									aria-label="Remove"
									onClick={() =>
										confirm({
											title: single ? "Remove this choice?" : "Remove this item?",
											description: "Its product and any backups go with it.",
											onConfirm: () =>
												deleteOption.mutate(option.id, {
													onSuccess: () => toast.push("Removed"),
													...notify,
												}),
										})
									}
								>
									<Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
								</Button>
							</div>
						) : null}

						<div className="space-y-4 px-3 py-3">
							<div>
								{primary.length === 0 ? (
									<Button
										variant="outline"
										size="sm"
										onClick={() =>
											setTarget({
												optionId: option.id,
												role: "primary",
												parentId: null,
												title: "Choose the product",
											})
										}
									>
										<Plus className="h-3.5 w-3.5" strokeWidth={2} />
										Choose product
									</Button>
								) : (
									<div className="divide-y divide-line">
										{primary.map((node) => (
											<ProductRow
												key={node.id}
												node={node}
												depth={0}
												onAddBundle={(parentId) =>
													setTarget({
														optionId: option.id,
														role: "primary",
														parentId,
														title: "Add an item to this bundle",
													})
												}
												onChange={(entry) =>
													setTarget({
														optionId: option.id,
														role: entry.role,
														parentId: null,
														title: "Change selection",
														entryId: entry.id,
													})
												}
												onQuantity={setQuantity}
												onSettings={setSettingsFor}
												onRemove={(entry) => removeProduct(entry)}
											/>
										))}
									</div>
								)}
							</div>

							<div>
								<p className="mb-1 flex items-center gap-1.5 text-xs text-ink-muted">
									If it is out of stock
									<Hint label="About backups">{BACKUP_HELP}</Hint>
								</p>
								{backups.length > 0 ? (
									<div className="divide-y divide-line">
										{backups.map((node) => (
											<ProductRow
												key={node.id}
												node={node}
												depth={0}
												onAddBundle={(parentId) =>
													setTarget({
														optionId: option.id,
														role: "backup",
														parentId,
														title: "Add an item to this bundle",
													})
												}
												onChange={(entry) =>
													setTarget({
														optionId: option.id,
														role: entry.role,
														parentId: null,
														title: "Change selection",
														entryId: entry.id,
													})
												}
												onQuantity={setQuantity}
												onSettings={setSettingsFor}
												onRemove={(entry) => removeProduct(entry)}
											/>
										))}
									</div>
								) : null}
								<Button
									variant="ghost"
									size="sm"
									className="mt-1"
									onClick={() =>
										setTarget({
											optionId: option.id,
											role: "backup",
											parentId: null,
											title: "Choose a backup",
										})
									}
								>
									<Plus className="h-3.5 w-3.5" strokeWidth={2} />
									{backups.length === 0 ? "Add a backup" : "Add another backup"}
								</Button>
							</div>

							{primary.length > 0 ? (
								<OptionGuideField
									campaignId={campaignId}
									giftId={giftId}
									optionId={option.id}
									guideLabel={option.guideLabel}
									guideAssetId={option.guideAssetId}
								/>
							) : null}
						</div>
					</div>
				);
			})}

			{canAutoAdd ? (
				<div className="border-t border-line pt-4">
					<Switch
						checked={gift?.autoAdd ?? false}
						onChange={(event) =>
							updateGift.mutate(
								{ giftId, autoAdd: event.target.checked },
								{ onSuccess: () => toast.push("Saved"), ...notify },
							)
						}
						label="Add to cart automatically"
						hint="The shopper receives everything in this gift without clicking anything."
					/>
				</div>
			) : null}

			<ProductPicker
				open={target !== null}
				onOpenChange={(open) => !open && setTarget(null)}
				onPick={pick}
				title={target?.title ?? "Choose a product"}
				busy={addProduct.isPending || updateProduct.isPending}
			/>

			<ItemSettingsDialog
				node={settingsFor}
				saving={updateProduct.isPending}
				onClose={() => setSettingsFor(null)}
				onSave={saveSettings}
			/>
		</div>
	);
};
